import { inject, Injectable } from "@angular/core";
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext} from "@ngxs/store";
import {
  ClearDonations,
  LoadDonations,
  RefreshCurrentPage,
  ReloadDonations} from "./donation.action";
import { Donation } from "../model/donation.model";
import { DonationService } from "../service/donation.service";
import { toDonation } from "../mapper/donation.mapper";

export class DonationStateModel {
  page: number;
  pageSize: number;
  total: number;
  pages: Record<number, Donation[]>;
  loading: boolean;
  error: Error | undefined;
}

@State<DonationStateModel>({
  name: "donations",
  defaults: {
    page: 1,
    pageSize: 50,
    total: 0,
    pages: {},
    loading: true,
    error: undefined}})
@Injectable()
export class DonationState {
  donationService = inject(DonationService);

  @Selector()
  static isLoading(state: DonationStateModel) {
    return state.loading;
  }

  @Selector()
  static hasError(state: DonationStateModel) {
    return !!state.error;
  }

  @Selector()
  static currentPageData(state: DonationStateModel) {
    return state.pages[state.page] || [];
  }

  static pageData(page: number) {
    return createSelector(
      [DonationState],
      (state: DonationStateModel) => state.pages[page] || [],
    );
  }

  @Selector()
  static pagination(state: DonationStateModel) {
    const pageSize = state.pageSize || 50;
    return {
      page: state.page,
      pageSize: pageSize,
      total: state.total,
      totalPages: Math.ceil(state.total / pageSize) || 0};
  }

  @Action(LoadDonations)
  async loadDonations(
    ctx: StateContext<DonationStateModel>,
    action: LoadDonations,
  ) {
    const state = ctx.getState();
    const nextPage = action.page ?? state.page;
    const nextPageSize = action.pageSize ?? state.pageSize;
    const key = nextPage;

    console.info(state.pages[nextPage]);
    if (state.pages[nextPage]) {
      ctx.patchState({
        page: nextPage,
        pageSize: nextPageSize,
        loading: false,
        error: undefined});
      return;
    }

    // Inicia loading
    ctx.patchState({
      loading: true,
      page: nextPage,
      pageSize: nextPageSize,
      error: undefined});

    try {
      const response = await this.donationService.getDonations(
        nextPage,
        nextPageSize,
      );

      const donations = toDonation(response); // ajuste se retorna objeto
      const total = response.pagination.total;

      // Usa getState de novo para evitar sobrescrever pages em corrida
      const current = ctx.getState();
      ctx.patchState({
        loading: false,
        total,
        pages: {
          ...current.pages,
          [key]: donations}});
    } catch (error) {
      console.error("Erro ao carregar donations", error);
      ctx.patchState({
        loading: false,
        error: error as Error});
    }
  }

  @Action(ReloadDonations)
  reloadDonations(ctx: StateContext<DonationStateModel>) {
    ctx.patchState({
      page: 1,
      total: 0,
      pages: {},
      loading: true,
      error: undefined});
    return ctx.dispatch(new LoadDonations(1, 50));
  }

  @Action(RefreshCurrentPage)
  async refreshCurrentPage(ctx: StateContext<DonationStateModel>) {
    const state = ctx.getState();
    const currentPage = state.page;
    const currentPageSize = state.pageSize;

    // Atualização silenciosa - NÃO ativa loading para evitar animação na tela
    try {
      const response = await this.donationService.getDonations(
        currentPage,
        currentPageSize,
      );

      const donations = toDonation(response);
      const total = response.pagination.total;

      const current = ctx.getState();
      ctx.patchState({
        total,
        pages: {
          ...current.pages,
          [currentPage]: donations}});
    } catch (error) {
      console.error("Erro ao atualizar página atual", error);
      // Também não seta error para manter a atualização silenciosa
    }
  }

  @Action(ClearDonations)
  clearDonations(ctx: StateContext<DonationStateModel>) {
    ctx.patchState({
      page: 1,
      pageSize: 50,
      total: 0,
      pages: {},
      loading: true,
      error: undefined});
  }
}
